import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,

  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  Analytics,
  TrendingUp,
  People,
  Favorite,
  SwipeRight,
  Star,
  Assessment
} from '@mui/icons-material';
import axios from 'axios';

const AnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('30d');
  const [comprehensiveStats, setComprehensiveStats] = useState<any>(null);

  const fetchComprehensiveStats = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`https://api.sobcof.com/api/dashboard/comprehensive-stats?timeRange=${timeRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setComprehensiveStats(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching comprehensive stats:', error);
      setError('Analitik verileri alınamadı');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchComprehensiveStats();
  }, [fetchComprehensiveStats]);

  return (
    <Box sx={{ 
      flexGrow: 1, 
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden'
    }}>
      {/* App Bar */}
      <AppBar 
        position="static" 
        sx={{ 
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            mr: 2,
            width: 40,
            height: 40
          }}>
            <Analytics sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ 
            flexGrow: 1,
            fontWeight: 600,
            color: 'white'
          }}>
            Detaylı Analitik
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ 
        p: { xs: 1, sm: 2, md: 2 },
        width: '100%',
        maxWidth: '100%',
        mx: 0,
        px: { xs: 1, sm: 2, md: 3 }
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Time Range Selector */}
        <Box sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Zaman Aralığı</InputLabel>
            <Select
              value={timeRange}
              label="Zaman Aralığı"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="7d">Son 7 Gün</MenuItem>
              <MenuItem value="30d">Son 30 Gün</MenuItem>
              <MenuItem value="90d">Son 90 Gün</MenuItem>
              <MenuItem value="1y">Son 1 Yıl</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress size={60} />
          </Box>
        ) : comprehensiveStats ? (
          <>
            {/* Hero Section */}
            <Card
              elevation={0}
              sx={{
                background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
                color: 'white',
                borderRadius: 4,
                mb: 4,
                overflow: 'hidden',
                width: '100%',
                mx: 0
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      📊 Platform Özeti
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      {timeRange === '7d' ? 'Son 7 Gün' :
                       timeRange === '30d' ? 'Son 30 Gün' :
                       timeRange === '90d' ? 'Son 90 Gün' : 'Son 1 Yıl'} Analitik Raporu
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {comprehensiveStats.overview?.totalUsers || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Toplam Kullanıcı
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {comprehensiveStats.overview?.totalMatches || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Toplam Eşleşme
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {comprehensiveStats.engagement?.matchRate?.toFixed(1) || 0}%
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Eşleşme Oranı
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Key Metrics Cards */}
            <Grid container columnSpacing={10} sx={{ mb: 4, width: '100%', mx: 0, justifyContent: 'center' }}>
              {/* @ts-ignore */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.05) 0%, rgba(210, 105, 30, 0.05) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(139, 69, 19, 0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Avatar sx={{
                      bgcolor: 'rgba(139, 69, 19, 0.1)',
                      mx: 'auto',
                      mb: 2,
                      width: 60,
                      height: 60,
                      border: '3px solid rgba(139, 69, 19, 0.2)'
                    }}>
                      <People sx={{ fontSize: 30, color: '#8B4513' }} />
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#8B4513' }}>
                      {comprehensiveStats.overview?.activeUsers || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Aktif Kullanıcı
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      %{comprehensiveStats.userDemographics?.activeRate?.toFixed(1) || 0} aktiflik oranı
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* @ts-ignore */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(210, 105, 30, 0.05) 0%, rgba(205, 133, 63, 0.05) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(210, 105, 30, 0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Avatar sx={{
                      bgcolor: 'rgba(210, 105, 30, 0.1)',
                      mx: 'auto',
                      mb: 2,
                      width: 60,
                      height: 60,
                      border: '3px solid rgba(210, 105, 30, 0.2)'
                    }}>
                      <Favorite sx={{ fontSize: 30, color: '#D2691E' }} />
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#D2691E' }}>
                      {comprehensiveStats.overview?.totalMatches || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Toplam Eşleşme
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Son 30 günde
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* @ts-ignore */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(205, 133, 63, 0.05) 0%, rgba(244, 164, 96, 0.05) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(205, 133, 63, 0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Avatar sx={{
                      bgcolor: 'rgba(205, 133, 63, 0.1)',
                      mx: 'auto',
                      mb: 2,
                      width: 60,
                      height: 60,
                      border: '3px solid rgba(205, 133, 63, 0.2)'
                    }}>
                      <SwipeRight sx={{ fontSize: 30, color: '#CD853F' }} />
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#CD853F' }}>
                      {comprehensiveStats.engagement?.matchRate?.toFixed(1) || 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Eşleşme Oranı
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Başarı oranı
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* @ts-ignore */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(244, 164, 96, 0.05) 0%, rgba(255, 218, 185, 0.05) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(244, 164, 96, 0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Avatar sx={{
                      bgcolor: 'rgba(244, 164, 96, 0.1)',
                      mx: 'auto',
                      mb: 2,
                      width: 60,
                      height: 60,
                      border: '3px solid rgba(244, 164, 96, 0.2)'
                    }}>
                      <Star sx={{ fontSize: 30, color: '#F4A460' }} />
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#F4A460' }}>
                      {comprehensiveStats.engagement?.averageRating?.toFixed(1) || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Ortalama Puan
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Kullanıcı değerlendirmesi
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* @ts-ignore */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(255, 218, 185, 0.05) 0%, rgba(255, 228, 196, 0.05) 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(255, 218, 185, 0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Avatar sx={{
                      bgcolor: 'rgba(255, 218, 185, 0.1)',
                      mx: 'auto',
                      mb: 2,
                      width: 60,
                      height: 60,
                      border: '3px solid rgba(255, 218, 185, 0.2)'
                    }}>
                      <TrendingUp sx={{ fontSize: 30, color: '#DEB887' }} />
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#DEB887' }}>
                      {comprehensiveStats.engagement?.swipesByType?.like || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Toplam Beğeni
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Etkileşim sayısı
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Detailed Stats */}
            <Grid container columnSpacing={10} sx={{ width: '100%', mx: 0, justifyContent: 'center' }}>
              {/* @ts-ignore */}
              <Grid item xs={12} md={4}>
                <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 3, height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <People color="primary" />
                    Kullanıcı Demografisi
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Erkek Kullanıcı:</Typography>
                      <Chip label={comprehensiveStats.userDemographics?.byGender?.male || 0} color="primary" size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Kadın Kullanıcı:</Typography>
                      <Chip label={comprehensiveStats.userDemographics?.byGender?.female || 0} color="secondary" size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Premium Oranı:</Typography>
                      <Chip label={`${comprehensiveStats.userDemographics?.premiumRate?.toFixed(1) || 0}%`} color="warning" size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Aktif Oranı:</Typography>
                      <Chip label={`${comprehensiveStats.userDemographics?.activeRate?.toFixed(1) || 0}%`} color="success" size="small" />
                    </Box>
                  </Box>
                </Card>
              </Grid>

              {/* @ts-ignore */}
              <Grid item xs={12} md={4}>
                <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 3, height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp color="primary" />
                    Etkileşim İstatistikleri
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Toplam Beğeni:</Typography>
                      <Chip label={comprehensiveStats.engagement?.swipesByType?.like || 0} color="success" size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Toplam Geç:</Typography>
                      <Chip label={comprehensiveStats.engagement?.swipesByType?.pass || 0} color="default" size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Süper Beğeni:</Typography>
                      <Chip label={comprehensiveStats.engagement?.swipesByType?.super_like || 0} color="primary" size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Kafedeki Kullanıcı:</Typography>
                      <Chip label={comprehensiveStats.engagement?.usersInCafes || 0} color="warning" size="small" />
                    </Box>
                  </Box>
                </Card>
              </Grid>

              {/* @ts-ignore */}
              <Grid item xs={12} md={4}>
                <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 3, height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assessment color="primary" />
                    Moderasyon & Sosyal
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Bekleyen Raporlar:</Typography>
                      <Chip label={comprehensiveStats.moderation?.pendingReports || 0} color="warning" size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Çözülen Raporlar:</Typography>
                      <Chip label={comprehensiveStats.moderation?.resolvedReports || 0} color="success" size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Arkadaşlık Kabul Oranı:</Typography>
                      <Chip label={`${comprehensiveStats.social?.acceptanceRate?.toFixed(1) || 0}%`} color="info" size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Silinmiş Hesaplar:</Typography>
                      <Chip label={comprehensiveStats.overview?.deletedAccounts || 0} color="error" size="small" />
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', p: 5 }}>
            Analitik verileri yüklenemedi
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AnalyticsPage;
